import React from "react";
import {
  Box,
  Card,
  CardContent,
  Skeleton,
  useTheme,
  Tabs,
  Tab,
} from "@mui/material";

export const ViewCompanyEarningsSkeleton: React.FC = () => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        backgroundColor:
          theme.palette.mode === "dark" ? "#1a1a1a" : "#ffffff",
      }}
    >
      {/* Compact Header Skeleton */}
      <Box
        sx={{
          height: 64,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 3,
          borderBottom:
            theme.palette.mode === "dark"
              ? "1px solid rgba(255, 255, 255, 0.1)"
              : "1px solid #e2e8f0",
          backgroundColor:
            theme.palette.mode === "dark" ? "#1a1a1a" : "#ffffff",
        }}
      >
        {/* Left Section - Company Info */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Skeleton
            variant="circular"
            width={40}
            height={40}
            sx={{
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.1)"
                  : "rgba(0, 0, 0, 0.1)",
            }}
          />
          <Box>
            <Skeleton
              variant="text"
              width={120}
              height={20}
              sx={{
                mb: 0.5,
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.1)"
                    : "rgba(0, 0, 0, 0.1)",
              }}
            />
            <Skeleton
              variant="text"
              width={80}
              height={16}
              sx={{
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.08)"
                    : "rgba(0, 0, 0, 0.08)",
              }}
            />
          </Box>
          <Box
            sx={{
              width: "1px",
              height: "40px",
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.1)"
                  : "#e2e8f0",
              mx: 1,
            }}
          />
          <Skeleton
            variant="rectangular"
            width={250}
            height={36}
            sx={{
              borderRadius: 0,
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.05)"
                  : "rgba(0, 0, 0, 0.05)",
            }}
          />
        </Box>
      </Box>

      {/* Main Content Area */}
      <Box sx={{ display: "flex", flex: 1, overflow: "hidden" }}>
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            backgroundColor:
              theme.palette.mode === "dark" ? "#1a1a1a" : "#ffffff",
          }}
        >
          {/* Tab Navigation Skeleton */}
          <Box
            sx={{
              px: 3,
              paddingTop: 2,
              paddingBottom: 2,
              backgroundColor:
                theme.palette.mode === "dark" ? "#1a1a1a" : "#ffffff",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              minHeight: 60,
            }}
          >
            <Tabs
              value={0}
              sx={{
                "& .MuiTabs-indicator": {
                  display: "none",
                },
                "& .MuiTabs-flexContainer": {
                  gap: 1,
                },
                "& .MuiTab-root": {
                  textTransform: "none",
                  py: 0,
                  px: 2,
                  minHeight: 32,
                  fontSize: "0.875rem",
                  backgroundColor:
                    theme.palette.mode === "dark" ? "#212121" : "#f5f5f5",
                  border:
                    theme.palette.mode === "dark"
                      ? "1px solid rgba(255, 255, 255, 0.1)"
                      : "1px solid rgba(0, 0, 0, 0.1)",
                  borderRadius: 0.5,
                  "&.Mui-selected": {
                    color: "#22c55e",
                    borderColor: "#22c55e",
                  },
                },
              }}
            >
              <Tab label="Transcripts" />
              <Tab label="Earnings" />
            </Tabs>

            <Box sx={{ display: "flex", gap: 2 }}>
              <Skeleton
                variant="rectangular"
                width={160}
                height={32}
                sx={{
                  borderRadius: 2,
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.1)"
                      : "rgba(0, 0, 0, 0.1)",
                }}
              />
              <Skeleton
                variant="rectangular"
                width={200}
                height={32}
                sx={{
                  borderRadius: 2,
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.1)"
                      : "rgba(0, 0, 0, 0.1)",
                }}
              />
            </Box>
          </Box>

          {/* Content Area - Exactly matches TranscriptDisplay structure */}
          <Box
            sx={{
              flex: 1,
              overflow: "auto",
              backgroundColor:
                theme.palette.mode === "dark" ? "#1a1a1a" : "#ffffff",
              px: 3,
              pb: 3,
            }}
          >
            {/* Single TranscriptDisplay Card - matches actual component structure */}
            <Card
              elevation={0}
              sx={{
                borderRadius: 3,
                backgroundColor:
                  theme.palette.mode === "dark" ? "#212121" : "#f5f5f5",
                border:
                  theme.palette.mode === "dark"
                    ? "1px solid rgba(255, 255, 255, 0.1)"
                    : "1px solid rgba(0, 0, 0, 0.1)",
                height: "calc(100vh - 160px)",
                minHeight: "600px",
                display: "flex",
                flexDirection: "column",
                overflowY: "auto",
              }}
            >
              <CardContent
                sx={{
                  p: 0,
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  overflowY: "auto",
                }}
              >
                {/* Scrollable Content Container - matches TranscriptDisplay */}
                <Box
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: "scroll",
                    p: 4,
                    "&::-webkit-scrollbar": {
                      width: 8,
                    },
                    "&::-webkit-scrollbar-track": {
                      background:
                        theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.05)"
                          : "rgba(0, 0, 0, 0.05)",
                      borderRadius: 4,
                    },
                    "&::-webkit-scrollbar-thumb": {
                      background:
                        theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.2)"
                          : "rgba(0, 0, 0, 0.2)",
                      borderRadius: 4,
                      "&:hover": {
                        background:
                          theme.palette.mode === "dark"
                            ? "rgba(255, 255, 255, 0.3)"
                            : "rgba(0, 0, 0, 0.3)",
                      },
                    },
                  }}
                >
                  {/* Centered Report Header - matches TranscriptDisplay */}
                  <Box sx={{ mb: 4, textAlign: "center" }}>
                    {/* Company Logo */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        mb: 3,
                      }}
                    >
                      <Skeleton
                        variant="circular"
                        width={80}
                        height={80}
                        sx={{
                          backgroundColor:
                            theme.palette.mode === "dark"
                              ? "rgba(255, 255, 255, 0.1)"
                              : "rgba(0, 0, 0, 0.1)",
                        }}
                      />
                    </Box>

                    {/* Company Name and Quarter Title */}
                    <Skeleton
                      variant="text"
                      width="60%"
                      height={50}
                      sx={{
                        mb: 2,
                        mx: "auto",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255, 255, 255, 0.1)"
                            : "rgba(0, 0, 0, 0.1)",
                      }}
                    />

                    {/* Date */}
                    <Skeleton
                      variant="text"
                      width="30%"
                      height={20}
                      sx={{
                        mx: "auto",
                        mb: 3,
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255, 255, 255, 0.08)"
                            : "rgba(0, 0, 0, 0.08)",
                      }}
                    />

                    {/* Divider line */}
                    <Box
                      sx={{
                        width: "200px",
                        height: "1px",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255, 255, 255, 0.1)"
                            : "rgba(0, 0, 0, 0.1)",
                        mx: "auto",
                      }}
                    />
                  </Box>

                  {/* Content Text Skeleton */}
                  {Array.from({ length: 15 }).map((_, index) => (
                    <Skeleton
                      key={index}
                      variant="text"
                      width={index % 4 === 0 ? "80%" : "100%"}
                      height={20}
                      sx={{
                        mb: 1,
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255, 255, 255, 0.08)"
                            : "rgba(0, 0, 0, 0.08)",
                      }}
                    />
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};